import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import {
  type Company,
  FEATURE_FLAGS,
  FLAG_GROUP_LABEL,
  FLAG_KEYS,
  type FlagKey,
  type Flags,
  formatDate,
  STAFF_ROLE_LABEL,
  STAFF_ROLES,
  type StaffMember,
  type StaffRole,
  TAX_FORM_LABEL,
  TAX_FORMS,
  type TaxForm,
} from '@reikraft/shared';
import { Field, Icon, Modal, Notice, Panel, Switch, Tabs, useToast } from '@reikraft/ui';
import { api, errorText } from '../api';
import { FormMessage, Hint, PageHead, useFormErrors, useStaff } from '../components/common';
import { formatMoney, kopToRub, rubToKop } from '../format';
import { useUrlState } from '../hooks';
import { useMe } from '../session';

export function SettingsPage() {
  const me = useMe();
  const [state, set] = useUrlState({ tab: 'company' });
  return (
    <>
      <PageHead title="Настройки" sub={me.role === 'admin' ? undefined : 'Менять настройки может администратор'} />
      <Tabs
        label="Разделы настроек"
        value={state.tab}
        onChange={(tab) => set({ tab })}
        items={[
          { id: 'company', label: 'Компания' },
          { id: 'flags', label: 'Функции' },
          { id: 'users', label: 'Сотрудники' },
        ]}
      />
      {state.tab === 'company' && <CompanyForm />}
      {state.tab === 'flags' && <FlagsForm />}
      {state.tab === 'users' && <Users />}
    </>
  );
}

// ─── Компания ────────────────────────────────────────────────────────

const MONEY_FIELDS = ['diagPriceKop', 'priceFromMechKop', 'priceFromHpsKop', 'priceFromEpsKop', 'installPriceKop', 'npdLimitKop'] as const;
type MoneyField = (typeof MONEY_FIELDS)[number];

function CompanyForm() {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const errs = useFormErrors();
  const [c, setC] = useState<Company>(me.company);
  const [money, setMoney] = useState<Record<MoneyField, string>>(
    () => Object.fromEntries(MONEY_FIELDS.map((k) => [k, kopToRub(me.company[k])])) as Record<MoneyField, string>,
  );
  useEffect(() => setC(me.company), [me.company]);
  const readOnly = me.role !== 'admin';
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { ...c };
      for (const k of MONEY_FIELDS) body[k] = rubToKop(money[k]) ?? (k === 'npdLimitKop' ? 2_400_000_00 : null);
      return api.put<Company>('/admin/settings/company', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      toast('Настройки сохранены — сайт обновится в течение минуты');
      errs.clear();
    },
    onError: (e) => errs.set(e),
  });
  const text = (k: keyof Company) => ({
    value: (c[k] as string | null) ?? '',
    onChange: (e: { target: { value: string } }) => setC({ ...c, [k]: e.target.value }),
    readOnly,
  });
  const num = (k: keyof Company) => ({
    value: String(c[k] ?? ''),
    onChange: (e: { target: { value: string } }) => setC({ ...c, [k]: Number(e.target.value) || 0 }),
    readOnly,
  });
  const mon = (k: MoneyField) => ({
    value: money[k],
    onChange: (e: { target: { value: string } }) => setMoney({ ...money, [k]: e.target.value }),
    readOnly,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const err = errs.errors;
  return (
    <form onSubmit={submit} className="rk-stack" style={{ ['--rk-gap' as string]: '20px' }}>
      <FormMessage message={errs.message} />
      <Hint>
        Значения в [квадратных скобках] — заглушки дизайна. Замените их реальными данными: они показываются на сайте, в кабинете и в
        документах.
      </Hint>
      <Panel num="01" title="Бренд и контакты">
        <div className="rk-grid-form" style={{ ['--rk-min' as string]: '240px' }}>
          <Field label="Название (бренд)" error={err.brand}>
            <input className="rk-input" {...text('brand')} />
          </Field>
          <Field label="Подзаголовок у логотипа">
            <input className="rk-input" {...text('tagline')} />
          </Field>
          <Field label="Телефон" error={err.phone}>
            <input className="rk-input rk-input--mono" type="tel" {...text('phone')} />
          </Field>
          <Field label="E-mail" error={err.email}>
            <input className="rk-input" type="email" {...text('email')} />
          </Field>
          <Field label="Город">
            <input className="rk-input" {...text('city')} />
          </Field>
          <Field label="Адрес мастерской">
            <input className="rk-input" {...text('address')} />
          </Field>
          <Field label="Как проехать">
            <input className="rk-input" {...text('addressNote')} />
          </Field>
          <Field
            label="Координаты мастерской"
            hint="Для кнопки «Построить маршрут»: в Яндекс Картах нажмите на мастерскую и скопируйте координаты"
            error={err.geoPoint}
          >
            <input className="rk-input" placeholder="55.796127, 49.106414" inputMode="decimal" {...text('geoPoint')} />
          </Field>
          <Field
            label="Карта (ссылка для встраивания)"
            hint="Необязательно: без неё карта строится по координатам. Конструктор карт Яндекса → «Получить код» → вставьте код целиком или ссылку"
          >
            <input className="rk-input" {...text('mapEmbedUrl')} />
          </Field>
        </div>
      </Panel>
      <Panel num="02" title="График">
        <div className="rk-grid-form" style={{ ['--rk-min' as string]: '200px' }}>
          <Field label="Будни">
            <input className="rk-input" {...text('scheduleWeekdays')} />
          </Field>
          <Field label="Суббота">
            <input className="rk-input" {...text('scheduleSaturday')} />
          </Field>
          <Field label="Кратко (в шапке сайта)">
            <input className="rk-input" {...text('scheduleShort')} />
          </Field>
          <Field label="Перезвоним за, минут">
            <input className="rk-input rk-input--mono" type="number" min={0} {...num('callbackMinutes')} />
          </Field>
        </div>
      </Panel>
      <Panel num="03" title="Цены и сроки на сайте">
        <div className="rk-grid-form" style={{ ['--rk-min' as string]: '180px' }}>
          <Field label="Диагностика, ₽">
            <input className="rk-input rk-input--mono" inputMode="numeric" {...mon('diagPriceKop')} />
          </Field>
          <Field label="Ремонт механики от, ₽">
            <input className="rk-input rk-input--mono" inputMode="numeric" {...mon('priceFromMechKop')} />
          </Field>
          <Field label="Ремонт ГУР от, ₽">
            <input className="rk-input rk-input--mono" inputMode="numeric" {...mon('priceFromHpsKop')} />
          </Field>
          <Field label="Ремонт ЭУР от, ₽">
            <input className="rk-input rk-input--mono" inputMode="numeric" {...mon('priceFromEpsKop')} />
          </Field>
          <Field label="Установка рейки, ₽">
            <input className="rk-input rk-input--mono" inputMode="numeric" {...mon('installPriceKop')} />
          </Field>
          <Field label="Гарантия, месяцев">
            <input className="rk-input rk-input--mono" type="number" min={0} max={60} {...num('warrantyMonths')} />
          </Field>
          <Field label="Срок ремонта от, дней">
            <input className="rk-input rk-input--mono" type="number" min={0} {...num('repairDaysFrom')} />
          </Field>
          <Field label="Отгрузка (текст)">
            <input className="rk-input" {...text('shippingNote')} />
          </Field>
          <Field label="Резерв рейки, часов">
            <input className="rk-input rk-input--mono" type="number" min={1} max={720} {...num('reservationHours')} />
          </Field>
        </div>
      </Panel>
      <Panel num="04" title="Реквизиты и налоги">
        <div className="rk-grid-form" style={{ ['--rk-min' as string]: '220px' }}>
          <Field label="Форма">
            <select
              className="rk-select"
              value={c.taxForm}
              disabled={readOnly}
              onChange={(e) => setC({ ...c, taxForm: e.target.value as TaxForm })}
            >
              {TAX_FORMS.map((t) => (
                <option key={t} value={t}>
                  {TAX_FORM_LABEL[t]}
                </option>
              ))}
            </select>
          </Field>
          <Field label={c.taxForm === 'ooo' ? 'Название юрлица' : 'ФИО'}>
            <input className="rk-input" {...text('legalName')} />
          </Field>
          <Field label="ИНН">
            <input className="rk-input rk-input--mono" inputMode="numeric" {...text('inn')} />
          </Field>
          {c.taxForm !== 'npd' && (
            <Field label={c.taxForm === 'ip' ? 'ОГРНИП' : 'ОГРН'}>
              <input className="rk-input rk-input--mono" inputMode="numeric" {...text('ogrn')} />
            </Field>
          )}
          {c.taxForm === 'npd' && (
            <Field label="Лимит дохода НПД в год, ₽" hint="По закону — 2 400 000 ₽">
              <input className="rk-input rk-input--mono" inputMode="numeric" {...mon('npdLimitKop')} />
            </Field>
          )}
          <Field label="Часовой пояс">
            <select className="rk-select" value={c.timezone} disabled={readOnly} onChange={(e) => setC({ ...c, timezone: e.target.value })}>
              {[
                'Europe/Kaliningrad',
                'Europe/Moscow',
                'Europe/Samara',
                'Asia/Yekaterinburg',
                'Asia/Omsk',
                'Asia/Novosibirsk',
                'Asia/Krasnoyarsk',
                'Asia/Irkutsk',
                'Asia/Yakutsk',
                'Asia/Vladivostok',
              ].map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Банковские реквизиты (для счетов)" style={{ marginTop: 12 }}>
          <textarea className="rk-textarea" rows={3} {...text('bankDetails')} />
        </Field>
        {c.taxForm === 'npd' && c.npdLimitKop && (
          <Hint className="">
            Лимит используется для предупреждений в «Финансах»: {formatMoney(c.npdLimitKop)} в год на каждого самозанятого.
          </Hint>
        )}
      </Panel>
      {!readOnly && (
        <div>
          <button type="submit" className="rk-btn rk-btn--primary rk-btn--lg" disabled={save.isPending}>
            Сохранить
          </button>
        </div>
      )}
    </form>
  );
}

// ─── Функции (фича-флаги) ────────────────────────────────────────────

function FlagsForm() {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const save = useMutation({
    mutationFn: (patch: Partial<Flags>) => api.put<Flags>('/admin/settings/flags', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
    onError: (e) => toast(errorText(e), { error: true }),
  });
  const groups = Object.keys(FLAG_GROUP_LABEL) as (keyof typeof FLAG_GROUP_LABEL)[];
  const readOnly = me.role !== 'admin';
  return (
    <div className="rk-stack" style={{ ['--rk-gap' as string]: '20px' }}>
      <Notice label="Как это работает">
        Все части системы уже есть и работают. Выключенная функция скрыта на сайте, в кабинете и в админке, а её адреса в API отвечают
        «функция выключена». Включайте, когда она понадобится.
      </Notice>
      {groups.map((g) => (
        <Panel key={g} title={FLAG_GROUP_LABEL[g]} bodyless>
          <div className="rk-rows">
            {FLAG_KEYS.filter((k) => FEATURE_FLAGS[k].group === g).map((k) => {
              const def = FEATURE_FLAGS[k];
              const missing = (def.requires ?? []).filter((r) => !me.flags[r as FlagKey]);
              return (
                <div key={k} className="rk-row" style={{ padding: '14px 18px', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                  <span className="rk-grow rk-td--stack">
                    <b>{def.title}</b>
                    <span className="rk-muted" style={{ fontSize: 13 }}>
                      {def.description}
                    </span>
                    {missing.length > 0 && me.flags[k] && (
                      <span style={{ fontSize: 12, color: 'var(--warn)' }}>
                        Не действует, пока выключено: {missing.map((r) => FEATURE_FLAGS[r as FlagKey].title).join(', ')}
                      </span>
                    )}
                  </span>
                  <Switch
                    size="lg"
                    checked={me.flags[k]}
                    disabled={readOnly || save.isPending}
                    label={def.title}
                    onChange={(v) => save.mutate({ [k]: v })}
                  />
                </div>
              );
            })}
          </div>
        </Panel>
      ))}
    </div>
  );
}

// ─── Сотрудники ──────────────────────────────────────────────────────

function Users() {
  const me = useMe();
  const staff = useStaff();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api.patch<StaffMember[]>(`/admin/staff/${id}`, body),
    onSuccess: (data) => qc.setQueryData(['staff'], data),
    onError: (e) => toast(errorText(e), { error: true }),
  });
  const admin = me.role === 'admin';
  return (
    <Panel
      title="Сотрудники"
      bodyless
      actions={
        admin && (
          <button type="button" className="rk-btn rk-btn--sm" onClick={() => setOpen(true)}>
            <Icon name="plus" size={14} /> Сотрудник
          </button>
        )
      }
    >
      <div className="rk-rows">
        {(staff.data ?? []).map((s) => (
          <div key={s.id} className="rk-row" style={{ padding: '12px 18px', justifyContent: 'space-between' }}>
            <span className="rk-td--stack" style={{ opacity: s.isActive ? 1 : 0.5 }}>
              <b>
                {s.name}
                {s.id === me.id ? ' (вы)' : ''}
              </b>
              <span className="rk-muted" style={{ fontSize: 12 }}>
                {s.email} · {s.lastLoginAt ? `входил ${formatDate(s.lastLoginAt)}` : 'не входил'}
              </span>
            </span>
            <span className="rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
              <select
                className="rk-select"
                style={{ width: 170, height: 36 }}
                value={s.role}
                disabled={!admin}
                aria-label={`Роль: ${s.name}`}
                onChange={(e) => update.mutate({ id: s.id, body: { role: e.target.value as StaffRole } })}
              >
                {STAFF_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {STAFF_ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
              {admin && (
                <>
                  <button
                    type="button"
                    className="rk-btn rk-btn--sm"
                    onClick={() => {
                      const pw = window.prompt(`Новый пароль для ${s.name} (минимум 8 символов)`);
                      if (pw) update.mutate({ id: s.id, body: { password: pw } }, { onSuccess: () => toast('Пароль изменён') });
                    }}
                  >
                    Пароль
                  </button>
                  {s.id !== me.id && (
                    <button
                      type="button"
                      className="rk-btn rk-btn--sm"
                      onClick={() => update.mutate({ id: s.id, body: { isActive: !s.isActive } })}
                    >
                      {s.isActive ? 'Отключить' : 'Включить'}
                    </button>
                  )}
                </>
              )}
            </span>
          </div>
        ))}
      </div>
      <StaffModal open={open} onClose={() => setOpen(false)} />
    </Panel>
  );
}

function StaffModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [f, setF] = useState({ name: '', email: '', phone: '', role: 'master' as StaffRole, password: '' });
  const errs = useFormErrors();
  const qc = useQueryClient();
  const toast = useToast();
  useEffect(() => {
    if (open) {
      setF({ name: '', email: '', phone: '', role: 'master', password: '' });
      errs.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const save = useMutation({
    mutationFn: () => api.post<StaffMember[]>('/admin/staff', f),
    onSuccess: (data) => {
      qc.setQueryData(['staff'], data);
      toast('Сотрудник добавлен');
      onClose();
    },
    onError: (e) => errs.set(e),
  });
  const bind = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Новый сотрудник"
      footer={
        <button type="button" className="rk-btn rk-btn--primary" disabled={save.isPending} onClick={() => save.mutate()}>
          Добавить
        </button>
      }
    >
      <FormMessage message={errs.message} />
      <div className="rk-grid-form">
        <Field label="Имя" error={errs.errors.name}>
          <input className="rk-input" {...bind('name')} />
        </Field>
        <Field label="Роль">
          <select className="rk-select" {...bind('role')}>
            {STAFF_ROLES.map((r) => (
              <option key={r} value={r}>
                {STAFF_ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="E-mail (логин)" error={errs.errors.email}>
          <input className="rk-input" type="email" {...bind('email')} />
        </Field>
        <Field label="Телефон" error={errs.errors.phone}>
          <input className="rk-input rk-input--mono" type="tel" {...bind('phone')} />
        </Field>
        <Field label="Пароль" error={errs.errors.password} hint="Минимум 8 символов">
          <input className="rk-input rk-input--mono" type="text" autoComplete="new-password" {...bind('password')} />
        </Field>
      </div>
    </Modal>
  );
}
